<template>
  <div>
    <row-col-text-field
      :value="software.name"
      :label="$t('form.name')"
      :rules="rules.char_required_max_120"
      :required="true"
      :readonly="readonly"
      @input="update('name', $event)"
    />

    <MultilingualTextField
      :value="software.short_description"
      :label="$t('form.description_short_software')"
      :rules="rules.char_max_180"
      :required="true"
      :readonly="readonly"
      :hint="$t('form.description_short_software_hint')"
      :persistent-hint="!readonly"
      entity-type="software"
      field="short_description"
      @input="update('short_description', $event)"
    />

    <v-divider class="mt-6 mb-5" />
    <h5 class="mb-2">{{ $t("form.software_platforms_services_section") }}</h5>
    <v-checkbox
      :input-value="software.publish_in_platforms_and_services"
      :label="$t('form.publish_in_platforms_and_services')"
      :readonly="readonly"
      hide-details
      @change="update('publish_in_platforms_and_services', Boolean($event))"
    />
    <p class="text--secondary text-caption mt-1">
      {{ $t("form.software_platforms_services_help") }}
    </p>
    <v-row>
      <v-col cols="12" md="6">
        <h5>{{ $t("form.software_image_squared") }}</h5>
        <p class="text--secondary text-caption mb-1">
          {{ $t("form.software_image_squared_hint") }}
        </p>
        <UploadImage
          v-if="allowImageUpload"
          :path="imageUploadPath"
          :url="imageUrl(software.image_squared)"
          :object="{ name: software.image_squared }"
          :object_created="imageUploadCreated"
          :info_text="$t('form.software_image_save_first')"
          name_field="image_squared"
          :placeholder="$t('form.software_image_squared')"
          :image-max-width="220"
          :image-max-height="220"
          @uploaded="imageUploaded"
        />
        <v-img
          v-if="allowImageUpload && !imageUploadCreated && software.image_squared"
          :src="imageUrl(software.image_squared)"
          max-width="240"
          max-height="240"
          contain
        />
        <v-img
          v-else-if="!allowImageUpload && software.image_squared"
          :src="imageUrl(software.image_squared)"
          max-width="240"
          max-height="240"
          contain
        />
      </v-col>
      <v-col cols="12" md="6">
        <h5>{{ $t("form.software_image_horizontal") }}</h5>
        <p class="text--secondary text-caption mb-1">
          {{ $t("form.software_image_horizontal_hint") }}
        </p>
        <UploadImage
          v-if="allowImageUpload"
          :path="imageUploadPath"
          :url="imageUrl(software.image_horizontal)"
          :object="{ name: software.image_horizontal }"
          :object_created="imageUploadCreated"
          :info_text="$t('form.software_image_save_first')"
          name_field="image_horizontal"
          :placeholder="$t('form.software_image_horizontal')"
          :image-max-width="420"
          :image-max-height="140"
          @uploaded="imageUploaded"
        />
        <v-img
          v-if="allowImageUpload && !imageUploadCreated && software.image_horizontal"
          :src="imageUrl(software.image_horizontal)"
          max-width="520"
          max-height="180"
          contain
        />
        <v-img
          v-else-if="!allowImageUpload && software.image_horizontal"
          :src="imageUrl(software.image_horizontal)"
          max-width="520"
          max-height="180"
          contain
        />
      </v-col>
    </v-row>
    <v-input
      v-if="!readonly"
      :value="platformsServicesRequirementsValid"
      :rules="[value => value || $t('form.software_platforms_services_validation')]"
    />

    <v-divider class="mt-6 mb-5" />
    <h5 class="mb-3">{{ $t("form.software_catalog_section") }}</h5>
    <v-row>
      <v-col cols="12" md="6" class="py-0">
        <h5>
          {{ $t("form.software_type") }}
          <v-chip class="ma-2" x-small outlined color="primary">
            {{ $t("form.mandatory") }}
          </v-chip>
        </h5>
        <v-select
          :value="software.software_type"
          :items="softwareTypes"
          item-text="text"
          item-value="value"
          :readonly="readonly"
          :disabled="readonly"
          :rules="rules.fieldRequired"
          outlined
          @input="update('software_type', $event)"
        />
      </v-col>
      <v-col cols="12" md="6" class="py-0">
        <h5>{{ $t("form.software_status") }}</h5>
        <v-select
          :value="software.status"
          :items="softwareStatuses"
          item-text="text"
          item-value="value"
          :readonly="readonly"
          :disabled="readonly"
          :rules="rules.fieldRequired"
          outlined
          @input="update('status', $event)"
        />
      </v-col>
    </v-row>

    <v-row>
      <col-date-field
        :value="software.launched_date"
        :label="$t('form.launched_date')"
        :required="true"
        :readonly="readonly"
        @input="update('launched_date', $event)"
      />
      <col-date-field
        :value="software.latest_release_date"
        :label="$t('form.software_latest_release_date')"
        :readonly="readonly"
        @input="update('latest_release_date', $event)"
      />
      <v-col cols="12" md="4" class="py-0">
        <row-col-text-field
          :value="software.current_version"
          :label="$t('form.software_current_version')"
          :rules="rules.char_max_80"
          :readonly="readonly"
          @input="update('current_version', $event)"
        />
      </v-col>
    </v-row>

    <div class="mb-3">
      <h5 class="mb-2">{{ $t("form.keywords") }}</h5>
      <ArrayFieldForm
        v-if="!readonly"
        :value="software.keywords"
        name-field="keywords"
        :label="$t('form.keywords')"
        @input="update('keywords', $event)"
      />
      <v-text-field v-else :value="displayArray(software.keywords)" disabled />
    </div>

    <div class="mb-3">
      <h5 class="mb-2">{{ $t("form.software_programming_languages") }}</h5>
      <ArrayFieldForm
        v-if="!readonly"
        :value="software.programming_languages"
        name-field="programming_languages"
        :label="$t('form.software_programming_language')"
        @input="update('programming_languages', $event)"
      />
      <v-text-field v-else :value="displayArray(software.programming_languages)" disabled />
    </div>

    <div class="mt-6">
      <MultilingualTextField
        :value="software.description"
        :label="$t('form.description_software')"
        :hint="$t('form.description_software_hint')"
        :persistent-hint="!readonly"
        :readonly="readonly"
        :rich="true"
        :editor-ready="editorReady"
        entity-type="software"
        field="description"
        @input="update('description', $event)"
      />
    </div>

    <v-divider class="mt-8 mb-5" />
    <h5 class="mb-2">{{ $t("form.software_availability_section") }}</h5>
    <v-row class="py-0">
      <v-col cols="12" md="6" class="py-0">
        <v-checkbox
          :input-value="software.local_execution_available"
          :label="$t('form.local_execution_available')"
          :readonly="readonly"
          hide-details
          @change="update('local_execution_available', Boolean($event))"
        />
      </v-col>
      <v-col cols="12" md="6" class="py-0">
        <v-checkbox
          :input-value="software.remote_access_available"
          :label="$t('form.remote_access_available')"
          :readonly="readonly"
          hide-details
          @change="update('remote_access_available', Boolean($event))"
        />
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12" md="6" class="py-0">
        <h5>{{ $t("form.software_source_access") }}</h5>
        <v-select
          :value="software.source_access"
          :items="sourceAccessOptions"
          item-text="text"
          item-value="value"
          :readonly="readonly"
          :disabled="readonly"
          outlined
          @input="update('source_access', $event)"
        />
      </v-col>
    </v-row>

    <row-col-text-area-field
      v-if="software.source_access === 'agreement' || software.source_access_instructions"
      :value="software.source_access_instructions"
      :label="$t('form.software_source_access_instructions')"
      :rules="rules.char_max_2000"
      :required="software.source_access === 'agreement'"
      :readonly="readonly"
      @input="update('source_access_instructions', $event)"
    />

    <row-col-select-multiple-field
      :value="software.platforms"
      data-source="platform"
      :label="$t('form.platforms_software')"
      :required="true"
      :readonly="readonly"
      @input="update('platforms', $event)"
    />

    <row-col-select-multiple-field
      :value="software.licenses"
      data-source="license"
      :label="$t('form.license_software')"
      :required="true"
      :readonly="readonly"
      @input="update('licenses', $event)"
    />

    <v-divider class="mt-6 mb-5" />
    <h5 class="mb-4">{{ $t("form.software_links_section") }}</h5>
    <SoftwareLinksField
      :links="software.links"
      :readonly="readonly"
      @input="update('links', $event)"
    />
    <v-input
      v-if="!readonly"
      :value="availabilityLinksValid"
      :rules="[value => value || $t('form.software_link_validation')]"
    />

    <v-divider class="mt-6 mb-5" />
    <h5 class="mb-3">{{ $t("form.software_identifiers_section") }}</h5>
    <v-row>
      <v-col cols="12" md="6" class="py-0">
        <row-col-text-field
          :value="software.doi"
          label="DOI"
          :rules="rules.doiRules"
          :readonly="readonly"
          @input="update('doi', $event)"
        />
      </v-col>
    </v-row>
    <row-col-text-area-field
      :value="software.preferred_citation"
      :label="$t('form.software_preferred_citation')"
      :rules="rules.char_max_2000"
      :readonly="readonly"
      @input="update('preferred_citation', $event)"
    />

    <v-divider class="mt-6 mb-5" />
    <h5>{{ $t("form.software_relations_section") }}</h5>
    <row-col-select-one-field
      :value="software.responsible_person"
      data-source="person"
      :label="$t('form.software_responsible_person')"
      :required="true"
      :readonly="readonly"
      @input="update('responsible_person', $event)"
    />
    <row-col-select-multiple-field
      v-for="relation in relationFields"
      :key="relation.field"
      :value="software[relation.field]"
      :data-source="relation.source"
      :label="$t(relation.label)"
      :required="relation.required"
      :readonly="readonly"
      @input="update(relation.field, $event)"
    />
  </div>
</template>

<script>
import validationRules from "@/mixins/validationRules";
import ArrayFieldForm from "@/components/reusables/ArrayFieldForm.vue";
import ColDateField from "@/components/reusables/ColDateField.vue";
import MultilingualTextField from "@/components/reusables/MultilingualTextField.vue";
import RowColSelectMultipleField from "@/components/reusables/RowColSelectMultipleField.vue";
import RowColSelectOneField from "@/components/reusables/RowColSelectOneField.vue";
import RowColTextAreaField from "@/components/reusables/RowColTextAreaField.vue";
import RowColTextField from "@/components/reusables/RowColTextField.vue";
import UploadImage from "@/components/layouts/UploadImage.vue";
import SoftwareLinksField from "@/components/software/SoftwareLinksField.vue";

export default {
  name: "SoftwareFields",
  components: {
    ArrayFieldForm,
    ColDateField,
    MultilingualTextField,
    RowColSelectMultipleField,
    RowColSelectOneField,
    RowColTextAreaField,
    RowColTextField,
    UploadImage,
    SoftwareLinksField
  },
  mixins: [validationRules],
  props: {
    software: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    editorReady: {
      type: Boolean,
      default: true
    },
    allowImageUpload: {
      type: Boolean,
      default: false
    },
    imageUploadCreated: {
      type: Boolean,
      default: false
    },
    imageUploadPath: {
      type: String,
      default: ""
    }
  },
  data: () => ({
    relationFields: [
      { field: "researchers", source: "person", label: "form.software_researchers", required: true },
      { field: "organizations", source: "organization", label: "form.software_organizations" },
      { field: "publications", source: "publication", label: "form.software_publications" },
      { field: "projects", source: "project", label: "form.software_projects" },
      { field: "research_areas", source: "research_area", label: "form.software_research_areas" },
      { field: "datasets", source: "dataset", label: "form.software_datasets" }
    ]
  }),
  computed: {
    softwareTypes() {
      return this.translatedOptions("software_types", [
        "web_application",
        "desktop_application",
        "mobile_application",
        "library",
        "api",
        "command_line",
        "plugin",
        "service",
        "other"
      ]);
    },
    softwareStatuses() {
      return this.translatedOptions("software_statuses", [
        "development",
        "active",
        "maintenance",
        "archived",
        "discontinued"
      ]);
    },
    sourceAccessOptions() {
      return this.translatedOptions("software_source_access_options", [
        "unavailable",
        "public",
        "agreement"
      ]);
    },
    availabilityLinksValid() {
      const types = new Set((this.software.links || []).map(link => link.link_type));
      if (this.software.remote_access_available && !types.has("remote_access")) {
        return false;
      }
      if (
        this.software.local_execution_available &&
        !["binary_download", "package", "container", "app_store"]
          .some(type => types.has(type)) &&
        !["public", "agreement"].includes(this.software.source_access)
      ) {
        return false;
      }
      if (
        this.software.source_access === "public" &&
        !["repository", "source_download"].some(type => types.has(type))
      ) {
        return false;
      }
      return true;
    },
    platformsServicesRequirementsValid() {
      if (!this.software.publish_in_platforms_and_services) {
        return true;
      }
      const listingLinkTypes = new Set(["website", "remote_access", "demo"]);
      const hasListingLink = (this.software.links || []).some(link =>
        listingLinkTypes.has(link.link_type)
      );
      return Boolean(
        this.software.image_squared &&
        this.software.image_horizontal &&
        hasListingLink
      );
    }
  },
  methods: {
    imageUrl(value) {
      const path = value && typeof value === "object" ? value.name : value;
      if (!path) {
        return "";
      }
      if (/^https?:\/\//i.test(path)) {
        return path;
      }
      const endpoint = new URL(
        this.$store.state.endpoint || window.location.origin,
        window.location.origin
      );
      if (path.startsWith("/")) {
        return new URL(path, endpoint.origin).toString();
      }
      endpoint.pathname = `${endpoint.pathname.replace(/\/+$/, "")}/`;
      return new URL(path, endpoint).toString();
    },
    imageUploaded({ field, url }) {
      this.update(field, url);
    },
    translatedOptions(group, values) {
      return values.map(value => ({
        value,
        text: this.$t(`form.${group}.${value}`)
      }));
    },
    displayArray(values) {
      const filtered = (values || []).filter(value => value && value.trim());
      return filtered.length ? filtered.join(", ") : "-";
    },
    update(field, value) {
      this.$emit("update", field, value);
    }
  }
};
</script>
